import React, { Fragment, useEffect, useState } from "react";

import { Spinner } from "react-bootstrap";

import CartBackImg from "../assets/images/cart-back.png";
import MailMessageImg from "../assets/images/mailmessage.png";
import InstaImg from "../assets/images/instagram.png";
import TelePhoneImg from "../assets/images/telephone.png";
import TwitterImg from "../assets/images/twitter.png";
import { useStaticPageContent } from "../hooks/useStaticPageContent";
import { staticContentDefaults } from "../data/staticContentDefaults";
import { getStaticContentImage } from "../utils/staticContentImages";
import CmsRichText, { cmsPlainText } from "../components/common/CmsRichText";
import { resolveFooterContactHref, isExternalFooterHref } from "../utils/footerContactUtils";

const defaultContactIcons = {
  "telephone.png": TelePhoneImg,
  "mailmessage.png": MailMessageImg,
  "instagram.png": InstaImg,
  "twitter.png": TwitterImg,
};

const ContactUs = () => {
  const pageContent = useStaticPageContent("contact-us");
  const banner =
    pageContent?.banner || staticContentDefaults["contact-us"].banner;
  const contactChannels = pageContent?.contactChannels?.length
    ? pageContent.contactChannels
    : staticContentDefaults["contact-us"].contactChannels;
  const sidebarHeading =
    pageContent?.sidebarHeading ||
    staticContentDefaults["contact-us"].sidebarHeading;
  const formHeading =
    pageContent?.formHeading ||
    staticContentDefaults["contact-us"].formHeading;
  const [inputData, setInputData] = useState({
    fullName: "",
    company: "",
    email: "",
    contactNumber: "",
    msg: "",
  });
  const [formError, setFormError] = useState(false);
  const [btnLoader, setBtnLoader] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { fullName, company, email, contactNumber, msg } = inputData;

    if (
      !fullName ||
      !company ||
      !contactNumber ||
      contactNumber?.length < 10 ||
      !email ||
      !emailRegex.test(email)
    ) {
      setFormError(true);
    } else {
      setFormError(false);
      try {
        setBtnLoader(true);
        let payload = {
          full_name: fullName,
          company: company,
          email: email,
          phone_number: contactNumber,
          message: msg,
        };
        const response = await fetch(
          `${import.meta.env.VITE_APP_BASE_URL}/api/user/contact-us`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );
        if (response?.ok) {
          setSuccessMsg("Successfully Submitted");
        }
        setBtnLoader(false);
        setInputData({
          fullName: "",
          company: "",
          email: "",
          contactNumber: "",
          msg: "",
        });
      } catch (error) {
        console.log("Error while hitting sign up API", error);
        setBtnLoader(false);
      }
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  }, []);

  return (
    <Fragment>
      <div className="banner-section cart-banner contact">
        <img src={getStaticContentImage(banner.image, CartBackImg)} alt="..." />
        <h1>
          <CmsRichText as="span" value={banner.title} />
        </h1>
      </div>

      <div className="contact-sec">
        <div className="container">
          <div className="row">
            <div className="col-12 col-md-4">
              <div className="cont-content">
                <div className="foot_head">
                  <CmsRichText as="span" value={sidebarHeading} />
                </div>
                <ul>
                  {contactChannels.map((channel) => {
                    const href =
                      resolveFooterContactHref(
                        channel.type,
                        channel.value,
                        channel.href
                      ) || "#";
                    const external = isExternalFooterHref(href);

                    return (
                      <li key={channel.type}>
                        <a
                          href={href}
                          target={external ? "_blank" : undefined}
                          rel={external ? "noopener noreferrer" : undefined}
                        >
                          <img
                            src={getStaticContentImage(
                              channel.icon,
                              defaultContactIcons[channel.icon] || TelePhoneImg
                            )}
                            alt="contactIcon"
                          />
                          <div className="details">
                            {cmsPlainText(channel.label)}{" "}
                            <span>{cmsPlainText(channel.value)}</span>
                          </div>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            <div className="col-12 col-md-8">
              <div className="contact-content">
                <h2 className="xl-font">
                  <CmsRichText as="span" value={formHeading} />
                </h2>
                <form
                  onSubmit={(e) => handleSubmit(e)}
                  className="contact-form"
                >
                  <div className="contact-form">
                    <div className="row">
                      <div className="col-md-6">
                        <input
                          className="form-control me-2"
                          type="text"
                          placeholder="Full Name"
                          value={inputData?.fullName}
                          onKeyDown={(e) => {
                            if (e.key === " ") {
                              e.preventDefault();
                            }
                          }}
                          onKeyPress={(e) => {
                            if (!/^[a-zA-Z \s]+$/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) =>
                            setInputData({
                              ...inputData,
                              fullName: e.target.value,
                            })
                          }
                        />
                        {formError && !inputData?.fullName && (
                          <span className="error">
                            Please enter your full name
                          </span>
                        )}
                      </div>
                      <div className="col-md-6">
                        <input
                          className="form-control me-2"
                          type="text"
                          placeholder="Company"
                          value={inputData?.company}
                          onKeyPress={(e) => {
                            if (!/^[a-zA-Z \s]+$/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) =>
                            setInputData({
                              ...inputData,
                              company: e.target.value,
                            })
                          }
                        />
                        {formError && !inputData?.company && (
                          <span className="error">
                            Please enter your company name
                          </span>
                        )}
                      </div>
                      <div className="col-md-6">
                        <input
                          className="form-control me-2"
                          type="text"
                          placeholder="Business Email"
                          value={inputData?.email}
                          onKeyDown={(e) => {
                            if (e.key === " ") {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) =>
                            setInputData({
                              ...inputData,
                              email: e.target.value,
                            })
                          }
                        />
                        {formError &&
                          (!inputData?.email ||
                            !emailRegex.test(inputData?.email)) && (
                            <span className="error">
                              {!inputData?.email
                                ? " Please enter your business email"
                                : !emailRegex.test(inputData?.email)
                                  ? "Invalid email address"
                                  : ""}
                            </span>
                          )}
                      </div>
                      <div className="col-md-6">
                        <input
                          className="form-control me-2"
                          type="tel"
                          placeholder="Contact Number"
                          maxlength={10}
                          value={inputData?.contactNumber}
                          onKeyPress={(e) => {
                            if (!/(?:^| )\d+(\.\d+)?(?=$| )/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) =>
                            setInputData({
                              ...inputData,
                              contactNumber: e.target.value,
                            })
                          }
                        />
                        {formError &&
                          (!inputData?.contactNumber ||
                            inputData?.contactNumber?.length < 10) && (
                            <span className="error">
                              {!inputData?.contactNumber
                                ? " Please enter your contact number"
                                : inputData?.contactNumber?.length < 10
                                  ? "Invalid contact number"
                                  : ""}
                            </span>
                          )}
                      </div>
                      <div className="col-md-12">
                        <textarea
                          className="form-control"
                          placeholder="Your Message or Interest?"
                          rows="3"
                          value={inputData?.msg}
                          onChange={(e) =>
                            setInputData({
                              ...inputData,
                              msg: e.target.value,
                            })
                          }
                        ></textarea>
                      </div>
                    </div>
                    <div className="login_form">
                      <button
                        type="submit"
                        className="blue-button"
                        disabled={btnLoader}
                      >
                        Send Us {btnLoader && <Spinner />}
                      </button>
                    </div>
                    {successMsg && (
                      <span className="success-alert">{successMsg}</span>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ContactUs;
